<!-- Call-to-action banner: left label | centre control | right label -->
<!-- Phases (see fullstack/claude/DESIGN.md Part C, ROADMAP.md § Frontend banner redesign): -->
<!-- in-person or online event upcoming: Register Now (Pay Now with a bank-transfer invoice -->
<!-- waiting; Complete Registration for a 2-for-1 place); registered → countdown (online: only -->
<!-- once registered for every upcoming online event; until then Register Now goes to the -->
<!-- missing one); -->
<!-- registered, in the hour before a session → the countdown links to /live; -->
<!-- online live window and registered → Join Live Now. -->
<!-- Nothing upcoming: Get Full Access; signed in with full access → banner hidden. -->

<!-- your_email+clerk_test@example.com  |  test code: 424242 -->

<template>
  <div id="banner" ref="banner" :class="{isHidden, isEntering}" :style="pillStyle">
    <span class="left-text" :style="labelStyle">
      <BannerLabel :to="labels.to" :lines="shown.left" :is-stacked="shown.isStacked" />
    </span>
    <div ref="centre" class="center-content">
      <JoinLiveNow v-if="phase === 'online' && isLive && isRegisteredForSession" />
      <SessionCountdown
        v-else-if="countdownStart"
        :start="countdownStart"
        :now="now"
        :is-link="phase === 'online' && isWarmUp" />
      <RegisterNow
        v-else-if="phase === 'online'"
        :for-event="unregisteredEventKey"
        :label="registerLabel" />
      <RegisterNow v-else-if="phase === 'in-person'" />
      <GetFullAccess v-else />
    </div>
    <span class="right-text" :style="labelStyle">
      <BannerLabel :to="labels.to" :lines="shown.right" :is-stacked="shown.isStacked" />
    </span>
  </div>
</template>

<script>
let hasAppeared = false; // module scope: fade in once per page load, not on remount
</script>

<script setup>
const log = (msg) => process.env.VUE_APP_DEV && console.log(`Banner: ${msg}`); // eslint-disable-line

import {inject, computed, nextTick, ref, watchEffect} from "vue";
import {useRoute} from "vue-router";
import {DateTime} from "luxon";
import RegisterNow from "@/components/CallToAction/RegisterNow.vue";
import JoinLiveNow from "@/components/CallToAction/JoinLiveNow.vue";
import GetFullAccess from "@/components/CallToAction/GetFullAccess.vue";
import SessionCountdown from "@/components/CallToAction/SessionCountdown.vue";
import BannerLabel from "@/components/CallToAction/BannerLabel.vue";
import {getBannerLabels} from "@/components/CallToAction/bannerLabels";
import useOnlineSessions from "@/composables/useOnlineSessions";
import useEventState from "@/composables/useEventState";
import useLocalEventDates from "@/composables/useLocalEventDates";
import useEventRegistration from "@/composables/useEventRegistration";
import useOnlineRegisterButton from "@/composables/useOnlineRegisterButton";
import useLibraryTotals from "@/composables/useLibraryTotals";
import useBannerFit from "@/composables/useBannerFit";
import {getFlagEmoji, getZoneCountry} from "@/composables/zoneCountries";

import {setCallToActionStyle} from "@/composables/useBackground"; // fit to call-to-action

const displayWidth = inject("displayWidth"); // from App.vue > composable/
const banner = ref(null);
const centre = ref(null);
const route = useRoute();

const {activeSession, isLive, isWarmUp, now, year} = useOnlineSessions();
const {isPreEvent, isOnlinePreEvent, onlineEventKey, onlineEventLabel, nextEventDate} =
  useEventState();
const {keyDates: localKeyDates, zone: viewerZone} = useLocalEventDates();
const {isRegisteredOnline, isRegisteredInPerson, hasFullAccess} =
  useEventRegistration(year);
const {totals} = useLibraryTotals();
const isEntering = !hasAppeared;
hasAppeared = true;

function getPhase() {
  if (isPreEvent.value) return "in-person";
  if (isOnlinePreEvent.value) return "online";
  return "library";
}

// "Nov 3, 18 & 19, 2026" → "Nov 3, 18 & 19": only the last comma separates the year
function getWithoutYear(label) {
  const index = label.lastIndexOf(",");
  return index === -1 ? label : label.slice(0, index);
}

function getEventMonth(currentPhase) {
  if (currentPhase === "in-person") return DateTime.fromJSDate(nextEventDate).toFormat("MMMM");
  if (currentPhase !== "online") return "";
  const localDates = localKeyDates.value[onlineEventKey.value]?.bannerDates; // the viewer's dates
  return localDates || getWithoutYear(onlineEventLabel.value);
}

const phase = computed(getPhase);
const isRegisteredForSession = computed(() => isRegisteredOnline(activeSession.value?.eventKey));
// Register Now's event and wording; the countdown shows only once they're registered for every
// upcoming event (none left to register for)
const {unregisteredEventKey, registerLabel, isRegisteredForAll} = useOnlineRegisterButton();
const isHidden = computed(() => {
  if (route.name === "Form") return true;
  return phase.value === "library" && hasFullAccess.value;
});

// the viewer's flag beside the dates when they're moved to their calendar (Seán, 2026-10-22: more
// compact than "Sydney local time", which it carries for a screen reader and on hover); a zone
// without a flag (zoneCountries.js), "Local time"
function getBannerNote() {
  if (phase.value !== "online") return "";
  const note = localKeyDates.value[onlineEventKey.value]?.localNote;
  if (!note) return "";
  const flag = getFlagEmoji(getZoneCountry(viewerZone.value));
  return flag ? {flag, text: note} : "Local time";
}

const labels = computed(() => {
  return getBannerLabels({
    phase: phase.value,
    year,
    eventMonth: getEventMonth(phase.value),
    eventNote: getBannerNote(),
    totals: totals.value,
    displayWidth: displayWidth.value
  });
});

// the labels as drawn: on one line where they fit (useBannerFit.js), else stacked
const fit = useBannerFit({banner, centre, labels: () => labels.value, displayWidth});
const shown = computed(() => {
  const {mode} = fit.value;
  const {inline} = labels.value;
  if (mode === "stacked") return labels.value;
  const rightByMode = {inline: inline.right, inlineShort: inline.rightShort,
    inlineCompact: inline.rightCompact};
  return {isStacked: false, left: inline.left, right: rightByMode[mode]};
});
// Register Now's side padding: trimmed only as far as the dates need, with the viewer's flag
// (useBannerFit.js; Seán, 2026-10-23)
const pillStyle = computed(() => {
  return fit.value.pillPadding ? {"--pill-padding-x": fit.value.pillPadding} : {};
});
const labelStyle = computed(() => {
  return {fontSize: fit.value.fontSize, letterSpacing: fit.value.letterSpacing};
});

const countdownStart = computed(() => {
  if (phase.value === "in-person" && isRegisteredInPerson.value) {
    return DateTime.fromJSDate(nextEventDate);
  }
  if (phase.value !== "online" || isLive.value || !isRegisteredForAll.value) return null;
  return activeSession.value.start;
});

// in rem (63–95px at the default 16px), so the banner scales with the text when a browser's
// default font size is set smaller or larger (seen on Firefox 115, 2026-10-15). The space above
// and below the content, less on narrower screens (Seán, 2026-10-22): about 10% less wide
// (26 → 23.5px), 15% between (wide 23 → 19.5, medium 25 → 21), 25% on a phone (14 → 10.5)
function getHeightForWidth(width) {
  log(`width = ${width}`);
  if (width === "narrow") return "3.9375rem";
  if (width === "medium") return "5.125rem";
  if (width === "wide") return "5.4375rem";
  if (width === "unlimited") return "5.9375rem";
  return "3.9375rem";
}

const bannerHeight = computed(() => {
  if (isHidden.value) return "0px";
  return getHeightForWidth(displayWidth.value);
});

function applyBannerHeight(height) {
  nextTick(() => {
    if (!banner.value) return log("Banner element not yet in DOM");
    banner.value.style.height = height;
    document.documentElement.style.setProperty("--cta-height", height);
    setCallToActionStyle(height, "black");
  });
}

watchEffect(() => {
  // log(`route.name = ${route.name}`);
  log(`bannerHeight = ${bannerHeight.value}`);
  applyBannerHeight(bannerHeight.value);
});
</script>

<style scoped>
#banner {
  display: flex;
  position: fixed; /* separate layer: page content scrolls underneath */
  top: 0;
  left: 0;
  right: 0;
  z-index: 50; /* above page content (<= 20), below modal overlays (100+) */
  background-color: black;
  justify-content: space-between; /* Keeps left and right text at the sides */
  align-items: center;
  color: white;
  font-size: 0.99rem;
  /* left and right labels on the same lines as <main>'s text: the page column (body, max-width
     56rem, centred; App.vue) plus main's side padding (Main.vue). Percentages are of the banner's
     width, which (fixed, left/right 0) excludes the scrollbar, as the page column does */
  --main-padding: 1rem;
  padding: 0 max(var(--main-padding), calc(50% - 28rem + var(--main-padding)));
  line-height: 1.05;
}
.narrow #banner {
  --main-padding: 0.8rem; /* as .narrow main */
}
:is(.wide #banner, .medium #banner) {
  font-size: clamp(1.2rem, 4vw, 1.73rem); /* min, preferred, max was: 3.12em; */
}
.unlimited #banner {
  font-size: 1.73rem;
  height: 5.9375rem; /* as getHeightForWidth */
}
.left-text {
  margin-right: auto;
}
.right-text {
  margin-left: auto;
}
.center-content {
  display: flex;
  justify-content: center;
  flex-grow: 1;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}
#lineBreak {
  display: block; /* Forces a new line */
}
#lineBreak span {
  display: inline; /* prevent above new line */
}
/* RegisterNow.vue's 1.33em, or what the fit gives (--pill-padding-x: down to a quarter) */
#banner .center-content > a {
  padding-left: var(--pill-padding-x, 1.33em);
  padding-right: var(--pill-padding-x, 1.33em);
}
.isHidden {
  display: none; /* Or 'visibility: hidden;' if you want to keep the space */
}
.medium #banner :deep(.banner-label.isStacked) {
  font-size: 0.65em;
}
#banner.isEntering {
  animation: fade-in 300ms ease-out;
}
@keyframes fade-in {
  from {
    opacity: 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  #banner.isEntering {
    animation: none;
  }
}
</style>
